import { useRuntimeVariables } from 'app-kit'
import { AxiosResponse, HttpStatusCode } from 'axios'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import {
  type DocumentSearchResult,
  type SearchEngine,
  type UnifiedDocumentSearchResult,
  UnifiedDocumentSearchResultSchema,
  UnifiedSearchRequestSchema,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { isAiDocumentSearchEngine } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useRef } from 'react'
import useSWR, { type KeyedMutator, type SWRConfiguration } from 'swr'

const SEARCH_ENGINE_PATHS: Record<SearchEngine, string> = {
  unified: '/api/v2/documents/search',
  docChat: '/api/v2/documents/chat',
}

export function isSearchEngine(value: string): value is SearchEngine {
  return value in SEARCH_ENGINE_PATHS
}

const SEARCH_SWR_DEFAULTS: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
}

interface DocumentSearchOptions {
  query: string
  site_id: string
  limit?: number
  conversation_id?: string
  request_id?: string
  preview_mode?: boolean
  model?: string
  thinking_level?: string
}

interface DocumentSearchHookOptions {
  enabled?: boolean
}

interface UseDocumentSearchResult {
  data: DocumentSearchResult | undefined
  error: Error | undefined
  isLoading: boolean
  isValidating: boolean
  mutate: KeyedMutator<DocumentSearchResult>
  searchLatencyMs: number | undefined
}

export function useDocumentSearch(
  searchOptions: DocumentSearchOptions,
  swrConfig?: SWRConfiguration<DocumentSearchResult, Error>,
  engine: SearchEngine = 'unified',
  options: DocumentSearchHookOptions = {}
): UseDocumentSearchResult {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { isPreviewMode } = usePipelineConfig()
  const { POWER_BACKEND_AI_URL } = useRuntimeVariables()
  const endpoint = `${POWER_BACKEND_AI_URL}${SEARCH_ENGINE_PATHS[engine]}`
  const { enabled = true } = options
  const { request_id: requestId, ...documentSearchOptions } = searchOptions
  const requestBodyOptions = {
    ...documentSearchOptions,
    preview_mode: isPreviewMode,
  }
  const requestKey = [
    endpoint,
    engine,
    documentSearchOptions.query,
    documentSearchOptions.site_id,
    String(documentSearchOptions.limit ?? 20),
    documentSearchOptions.conversation_id ?? '',
    requestId ?? '',
    String(isPreviewMode),
  ] as const
  const swrKey =
    enabled && documentSearchOptions.query && !isAiDocumentSearchEngine(engine)
      ? requestKey
      : null
  const latencyRef = useRef<number | undefined>()

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    DocumentSearchResult,
    Error,
    typeof requestKey | null
  >(
    swrKey,
    async ([endpoint]) => {
      const start = performance.now()
      const result = await postFetcher(
        endpoint,
        JSON.stringify(UnifiedSearchRequestSchema.parse(requestBodyOptions)),
        axios
      )
      latencyRef.current = Math.round(performance.now() - start)
      return result
    },
    { ...SEARCH_SWR_DEFAULTS, ...swrConfig }
  )

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
    searchLatencyMs: latencyRef.current,
  }
}

const postFetcher = async (
  url: string,
  body: string,
  axios: AuthedAxiosRequests
): Promise<UnifiedDocumentSearchResult> => {
  const res = await axios.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  })
  throwOnError(res)
  return UnifiedDocumentSearchResultSchema.parse(res.data)
}

function throwOnError(res: AxiosResponse): void {
  if (
    res.status !== HttpStatusCode.Ok &&
    res.status !== HttpStatusCode.NoContent
  ) {
    const error = new Error('An error occurred while fetching the data.')
    error.cause = res.data
    throw error
  }
}
